// Type-only on purpose — see the note in build-selected-solution.ts.
import type { SolutionVersion } from '../xstate/prostudio.api-service.types';

/** `DEPLOYMENT_STATUS.Finished` on the backend — the only status that means "live". */
const FINISHED = 'finished';

/** The subset of a `SolutionDeployment` needed here, kept structural for testability. */
export type DeploymentLike = {
  environment?: string | null;
  status?: string | null;
  deployedAt?: string | null;
  isRepublish?: boolean | null;
  deployedBy?: {
    firstName?: string | null;
    lastName?: string | null;
    picture?: string | null;
  } | null;
  solutionVersion?: {
    id?: string | null;
    name?: string | null;
    version?: number | null;
  } | null;
};

export type DeployedVersions = { live?: string; sandbox?: string };

const newestFirst = (a: DeploymentLike, b: DeploymentLike): number =>
  new Date(b.deployedAt ?? 0).getTime() - new Date(a.deployedAt ?? 0).getTime();

/**
 * Which version currently sits in each environment, replacing Bot Manager's
 * `getEnvBotVersions`.
 *
 * Only `finished` deployments count: a deployment that is still running, or that failed
 * (`failed`, or any `"<step> - Failed"`), has not changed what is deployed. Where an
 * environment has several finished deployments — every republish adds one — the newest by
 * `deployedAt` wins.
 */
export const deriveDeployedVersions = (
  deployments: (DeploymentLike | null | undefined)[] | null | undefined
): DeployedVersions => {
  const result: DeployedVersions = {};

  for (const environment of ['live', 'sandbox'] as const) {
    const name = (deployments ?? [])
      .filter(
        (deployment): deployment is DeploymentLike =>
          deployment?.status === FINISHED &&
          deployment?.environment === environment &&
          Boolean(deployment?.solutionVersion?.name)
      )
      .sort(newestFirst)[0]?.solutionVersion?.name;

    if (name) result[environment] = name;
  }

  return result;
};

/** The subset of a `SolutionVersion` this needs. */
export type VersionLike = {
  id?: string | null;
  name?: string | null;
  version?: number | null;
};

/**
 * The Versions page history list, rebuilt from `solutionVersions` + `solutionDeployments`.
 *
 * `postHistorySearch` — the Bot Manager query this replaces — no longer exists in the
 * schema at all, so publish history has to be reconstructed. One entry per deployment,
 * newest first, carrying the deploying user so the page can still show an avatar.
 * Versions that were never deployed contribute no entry, which matches the old
 * behaviour: history listed publishes, not versions.
 */
export const buildVersionHistory = (
  deployments: (DeploymentLike | null | undefined)[] | null | undefined
): SolutionVersion[] =>
  (deployments ?? [])
    .filter(
      (deployment): deployment is DeploymentLike =>
        Boolean(deployment?.solutionVersion?.name) &&
        deployment?.status === FINISHED
    )
    .sort(newestFirst)
    .map((deployment) => ({
      version: deployment.solutionVersion?.name ?? null,
      env: deployment.environment ?? '',
      time: deployment.deployedAt ?? '',
      first_name: deployment.deployedBy?.firstName ?? '',
      last_name: deployment.deployedBy?.lastName ?? '',
      picture: deployment.deployedBy?.picture ?? '',
      uuid: deployment.solutionVersion?.id ?? '',
    })) as SolutionVersion[];
